import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FilterOption } from '../../types/cms-filter.types';
import { CmsFilterIcon } from './cms-filter-icons';

@Component({
    selector: 'cms-filter-dropdown',
    templateUrl: './cms-filter-dropdown.component.html',
    styleUrls: ['./cms-filter-dropdown.component.scss'],
    standalone: false,
})
export class CmsFilterDropdownComponent implements OnChanges {
    @Input() label = '';
    @Input() menuId = '';
    @Input() icon: CmsFilterIcon | null = null;
    @Input() options: FilterOption[] = [];
    @Input() selectedIds: number[] = [];
    @Input() openMenuId: string | null = null;
    @Input() iconOnly = false;
    /** When true, flip the panel if it would overflow the list page container. */
    @Input() layoutCompact = false;
    public menuAlignEnd = false;

    constructor(private host: ElementRef<HTMLElement>) {}

    @Output() menuToggle = new EventEmitter<string>();
    @Output() selectionChange = new EventEmitter<number[]>();

    public get isOpen(): boolean {
        return this.openMenuId === this.menuId;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['openMenuId'] && !this.isOpen) {
            this.menuAlignEnd = false;
        }

        if (this.isOpen && (changes['openMenuId'] || changes['layoutCompact'])) {
            this.updateMenuAlign();
        }
    }

    @HostListener('window:resize')
    onResize(): void {
        if (this.isOpen) {
            this.updateMenuAlign();
        }
    }

    private updateMenuAlign(): void {
        if (!this.layoutCompact) {
            this.menuAlignEnd = false;
            return;
        }

        const viewport = this.host.nativeElement.ownerDocument.defaultView;
        const trigger = this.host.nativeElement.querySelector('.cms-filter-dropdown__trigger');
        const container = this.host.nativeElement.closest('.cms-list-page, .exercises-page') as HTMLElement | null;
        if (!viewport || !trigger || !container) {
            this.menuAlignEnd = false;
            return;
        }

        const triggerRect = trigger.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const panelWidth = Math.min(210, viewport.innerWidth - 24);
        this.menuAlignEnd = triggerRect.left + panelWidth > containerRect.right - 12;
    }

    public get selectedCount(): number {
        return this.selectedIds.length;
    }

    public get displayLabel(): string {
        return this.selectedCount > 0 ? `${this.label} (${this.selectedCount})` : this.label;
    }

    public toggleOpen(event: MouseEvent): void {
        event.stopPropagation();
        this.menuToggle.emit(this.menuId);
    }

    public isSelected(id: number): boolean {
        return this.selectedIds.includes(id);
    }

    public toggleOption(id: number, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        const next = this.isSelected(id)
            ? this.selectedIds.filter(selectedId => selectedId !== id)
            : [...this.selectedIds, id];
        this.selectionChange.emit(next);
    }
}
